'use client';

import { c as _c } from "react/compiler-runtime";
import { useSearchParams } from 'next/navigation';
import { FlexContainer, InfoTooltip } from '../../components';
import { CheckboxTableFilter } from './CheckboxTableFilter';
import { CustomTableFilter } from './CustomTableFilter';
import { DateRangeTableFilter } from './DateRangeTableFilter';
import { DateTableFilter } from './DateTableFilter';
import { InputTableFilter } from './InputTableFilter';
import { MultiSelectTableFilter } from './MultiSelectTableFilter';
import { MultiSelectWithGroupsTableFilter } from './MultiSelectWithGroupsTableFilter';
import { SearchTableFilter } from './SearchTableFilter';
import { SelectTableFilter } from './SelectTableFilter';
import { ToggleGroupTableFilter } from './ToggleGroupTableFilter';
import type { CheckboxTableFilterProps } from './CheckboxTableFilter';
import type { CustomFilterRenderProps, CustomTableFilterProps } from './CustomTableFilter';
import type { DateRangeTableFilterProps } from './DateRangeTableFilter';
import type { DateTableFilterProps } from './DateTableFilter';
import type { InputTableFilterProps } from './InputTableFilter';
import type { MultiSelectTableFilterProps } from './MultiSelectTableFilter';
import type { MultiSelectWithGroupsTableFilterProps } from './MultiSelectWithGroupsTableFilter';
import type { SearchTableFilterProps } from './SearchTableFilter';
import type { SelectTableFilterProps } from './SelectTableFilter';
import type { ToggleGroupTableFilterProps } from './ToggleGroupTableFilter';
import type { FilterOption } from './types';
type TableFilterProps = (InputTableFilterProps | CheckboxTableFilterProps | SelectTableFilterProps | MultiSelectTableFilterProps | SearchTableFilterProps | ToggleGroupTableFilterProps | MultiSelectWithGroupsTableFilterProps | DateRangeTableFilterProps | DateTableFilterProps | CustomTableFilterProps) & {
  infoTooltip?: string;
  filterType?: TableFilterType;
  /** Whether the filter grows to fill the row. Defaults to true. */
  grow?: boolean;
};
export type TableFilterType = 'rsc' | 'client';
function TableFilter(props) {
  const $ = _c(26);
  const {
    variant: t0,
    infoTooltip,
    shouldRender,
    grow: t1
  } = props;
  const variant = t0 === undefined ? "toggleGroup" : t0;
  const grow = t1 === undefined ? true : t1;
  const searchParams = useSearchParams();
  if (shouldRender && !shouldRender(searchParams ?? new URLSearchParams())) {
    return null;
  }
  let t2;
  if ($[0] !== infoTooltip) {
    t2 = infoTooltip ? <InfoTooltip tooltip={infoTooltip} /> : null;
    $[0] = infoTooltip;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const infoTooltipContent = t2;
  let filterComponent = null;
  bb0: switch (variant) {
    case "input":
      {
        const t3 = props as InputTableFilterProps;
        let t4;
        if ($[2] !== t3) {
          t4 = <InputTableFilter className="min-w-50" {...t3} />;
          $[2] = t3;
          $[3] = t4;
        } else {
          t4 = $[3];
        }
        filterComponent = t4;
        break bb0;
      }
    case "checkbox":
      {
        const t3 = props as CheckboxTableFilterProps;
        let t4;
        if ($[4] !== t3) {
          t4 = <CheckboxTableFilter {...t3} />;
          $[4] = t3;
          $[5] = t4;
        } else {
          t4 = $[5];
        }
        filterComponent = t4;
        break bb0;
      }
    case "select":
      {
        const t3 = props as SelectTableFilterProps;
        let t4;
        if ($[6] !== t3) {
          t4 = <SelectTableFilter {...t3} />;
          $[6] = t3;
          $[7] = t4;
        } else {
          t4 = $[7];
        }
        filterComponent = t4;
        break bb0;
      }
    case "search":
      {
        const t3 = props as SearchTableFilterProps;
        let t4;
        if ($[8] !== t3) {
          t4 = <SearchTableFilter {...t3} />;
          $[8] = t3;
          $[9] = t4;
        } else {
          t4 = $[9];
        }
        filterComponent = t4;
        break bb0;
      }
    case "toggleGroup":
      {
        const t3 = props as ToggleGroupTableFilterProps;
        let t4;
        if ($[10] !== t3) {
          t4 = <ToggleGroupTableFilter {...t3} />;
          $[10] = t3;
          $[11] = t4;
        } else {
          t4 = $[11];
        }
        filterComponent = t4;
        break bb0;
      }
    case "multiSelect":
      {
        const t3 = props as MultiSelectTableFilterProps;
        let t4;
        if ($[12] !== t3) {
          t4 = <MultiSelectTableFilter className="min-w-50" {...t3} />;
          $[12] = t3;
          $[13] = t4;
        } else {
          t4 = $[13];
        }
        filterComponent = t4;
        break bb0;
      }
    case "multiSelectWithGroups":
      {
        const t3 = props as MultiSelectWithGroupsTableFilterProps;
        let t4;
        if ($[14] !== t3) {
          t4 = <MultiSelectWithGroupsTableFilter className="min-w-50" {...t3} />;
          $[14] = t3;
          $[15] = t4;
        } else {
          t4 = $[15];
        }
        filterComponent = t4;
        break bb0;
      }
    case "dateRange":
      {
        const t3 = props as DateRangeTableFilterProps;
        let t4;
        if ($[16] !== t3) {
          t4 = <DateRangeTableFilter {...t3} />;
          $[16] = t3;
          $[17] = t4;
        } else {
          t4 = $[17];
        }
        filterComponent = t4;
        break bb0;
      }
    case "date":
      {
        const t3 = props as DateTableFilterProps;
        let t4;
        if ($[18] !== t3) {
          t4 = <DateTableFilter {...t3} />;
          $[18] = t3;
          $[19] = t4;
        } else {
          t4 = $[19];
        }
        filterComponent = t4;
        break bb0;
      }
    case "custom":
      {
        const t3 = props as CustomTableFilterProps;
        let t4;
        if ($[20] !== t3) {
          t4 = <CustomTableFilter {...t3} />;
          $[20] = t3;
          $[21] = t4;
        } else {
          t4 = $[21];
        }
        filterComponent = t4;
        break bb0;
      }
    default:
  }
  let t3;
  if ($[22] !== filterComponent || $[23] !== grow || $[24] !== infoTooltipContent) {
    t3 = filterComponent ? <FlexContainer className="w-full gap-2 md:w-auto empty:hidden" direction="row" grow={grow} items="center" shrink={true}>{filterComponent}{infoTooltipContent}</FlexContainer> : null;
    $[22] = filterComponent;
    $[23] = grow;
    $[24] = infoTooltipContent;
    $[25] = t3;
  } else {
    t3 = $[25];
  }
  return t3;
}
export default TableFilter;
export type { FilterOption, TableFilterProps, InputTableFilterProps, CheckboxTableFilterProps, SelectTableFilterProps, MultiSelectTableFilterProps, SearchTableFilterProps, ToggleGroupTableFilterProps, MultiSelectWithGroupsTableFilterProps, DateRangeTableFilterProps, DateTableFilterProps, CustomTableFilterProps, CustomFilterRenderProps };